export const RANK_NAMES = ['Cadet', 'Ensign', 'Lieutenant', 'Captain', 'Admiral'] as const;
export type RankName = typeof RANK_NAMES[number];

/** Base point thresholds to reach each rank, assuming a 10-minute reference match. */
export const BASE_THRESHOLDS = [0, 3, 7, 12, 18] as const;

export const REFERENCE_DURATION = 600; // 10 minutes in seconds

/** Prevent very short rounds from collapsing several ranks onto one kill. */
export const MIN_RANK_SCALE_DURATION = 300;

/**
 * Per-rank stat multipliers. Applied as: effectiveStat = baseStat * RANK_STAT_MULTS[rankIndex].
 * Covers: shield cap, shield regen, energy regen, primary fire rate, secondary reload.
 * Primary damage deliberately does not scale, avoiding a compound DPS snowball.
 */
export const RANK_STAT_MULTS = [1.0, 1.04, 1.08, 1.13, 1.18] as const;

/** Extra secondary ammo slots granted at each rank above the ship's base magazine. */
export const SECONDARY_AMMO_BONUS = [0, 0, 1, 1, 2] as const;

/**
 * Scale base point thresholds to match actual round duration.
 * Returns an array of 5 thresholds (index 0 = Cadet floor = 0, always).
 */
export function scaleThresholds(roundDuration: number): number[] {
  const scaleDuration = Math.max(MIN_RANK_SCALE_DURATION, roundDuration);
  return BASE_THRESHOLDS.map((t, i) =>
    i === 0 ? 0 : Math.ceil(t * scaleDuration / REFERENCE_DURATION),
  );
}

/** Derive rankIndex (0–4) from current points and scaled thresholds. */
export function computeRankIndex(points: number, thresholds: number[]): number {
  let rank = 0;
  for (let i = 1; i < thresholds.length; i++) {
    if (points >= thresholds[i]) rank = i;
    else break;
  }
  return rank;
}

/** Stat multiplier for the given rank index. */
export function getRankMult(rankIndex: number): number {
  return RANK_STAT_MULTS[rankIndex] ?? 1.0;
}
